/**
 * LeadDetailPage.tsx — the lead detail view + operator write UI (story E0-BO-LS8).
 *
 * Consumes `GET /api/leads/{id}` (via useLeadDetail) and renders the full
 * PII-MINIMISED record EXACTLY as the contract delivers it — masked phone,
 * masked/absent CPF, scrubbed normalized_data. This UI never attempts to unmask:
 * it displays `phone_e164_masked` / `cpf_masked` verbatim (Q3, WCAG-irrelevant
 * but LGPD-load-bearing). It reuses LeadStatusBadge + formatDateTime from LS7.
 *
 * Sections: the record, the funnel position, the conversation deep-link (or a
 * clear "no conversation yet" state), the append-only state-history timeline
 * (LeadStateTimeline), and the operator transition write UI (LeadTransitionForm,
 * offering ONLY allowed_transitions). Back-navigation returns to `#leads`.
 *
 * First-class states (spec §Error Handling): loading, notFound (404 — the lead
 * is gone, NOT a generic error), error (502 / transport, with retry), and the
 * shared UnauthorizedScreen on 401/403.
 */

import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { LeadDetail, LeadFact } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { LeadStatusBadge } from './LeadStatusBadge';
import { LeadStateTimeline } from './LeadStateTimeline';
import { LeadTransitionForm } from './LeadTransitionForm';
import { formatDateTime } from './formatDateTime';
import { useLeadDetail } from './useLeadDetail';
import { useLeadFacts, type LeadFactsPhase } from './useLeadFacts';
import floating from '../../lib/floating';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';

export interface LeadDetailPageProps {
  apiClient: ApiClient;
  leadId: string;
}

const BackLink = ({ label }: { label: string }) => (
  <a
    href="#leads"
    data-testid="lead-detail-back"
    className="inline-flex min-h-6 w-fit items-center gap-1 rounded text-meta text-gold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
  >
    <span aria-hidden="true">←</span> {label}
  </a>
);
const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-meta font-medium uppercase tracking-wide text-ink-3">{label}</dt>
    <dd className="text-body text-ink-1">{value}</dd>
  </div>
);
/** Scrubbed normalized_data — rendered EXACTLY as the DTO delivers it (Q3). */
const NormalizedDataBlock = ({ data }: { data: Record<string, unknown> | null }) => {
  const { t } = useTranslation();
  const entries = data != null ? Object.entries(data) : [];
  if (entries.length === 0) {
    return (
      <p data-testid="lead-normalized-empty" className="text-meta text-ink-3">
        {t('leadDetail.record.normalizedEmpty')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-meta font-medium uppercase tracking-wide text-ink-3">
        {t('leadDetail.record.normalized')}
      </h3>
      <dl
        data-testid="lead-normalized-data"
        className="grid grid-cols-1 gap-2 rounded-lg bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10 sm:grid-cols-2"
      >
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-col gap-0.5">
            <dt className="text-meta font-medium text-ink-3">{key}</dt>
            <dd className="text-body text-ink-1 break-words">
              {typeof value === 'string' ? value : JSON.stringify(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const RecordSection = ({ detail, locale }: { detail: LeadDetail; locale: string }) => {
  const { t } = useTranslation();
  const dash = '—';
  return (
    <section aria-labelledby="lead-record-heading" className="flex flex-col gap-3">
      <h2 id="lead-record-heading" className="sr-only">
        {t('leadDetail.record.heading')}
      </h2>
      <dl
        data-testid="lead-detail-record"
        className="grid grid-cols-1 gap-4 rounded-xl bg-surface-1 p-5 ring-1 ring-inset ring-ink-3/10 sm:grid-cols-2 lg:grid-cols-3"
      >
        <DetailItem
          label={t('leadDetail.record.funnelStage')}
          value={t(`funnelStage.${detail.funnel_stage}`)}
        />
        <DetailItem label={t('leadDetail.record.product')} value={detail.product_code ?? dash} />
        <DetailItem label={t('leadDetail.record.channel')} value={detail.channel} />
        <DetailItem label={t('leadDetail.record.phone')} value={detail.phone_e164_masked ?? dash} />
        <DetailItem label={t('leadDetail.record.cpf')} value={detail.cpf_masked ?? dash} />
        <DetailItem
          label={t('leadDetail.record.consent')}
          value={
            detail.lgpd_consent
              ? t('leadDetail.record.consentYes')
              : t('leadDetail.record.consentNo')
          }
        />
        <DetailItem
          label={t('leadDetail.record.consentAt')}
          value={formatDateTime(detail.lgpd_consent_at, locale)}
        />
        <DetailItem
          label={t('leadDetail.record.created')}
          value={formatDateTime(detail.created_at, locale)}
        />
        <DetailItem
          label={t('leadDetail.record.statusUpdated')}
          value={formatDateTime(detail.status_updated_at, locale)}
        />
        <DetailItem label={t('leadDetail.record.leadId')} value={detail.id} />
      </dl>
      <NormalizedDataBlock data={detail.normalized_data} />
    </section>
  );
};
const ConversationLinkBlock = ({ detail }: { detail: LeadDetail }) => {
  const { t } = useTranslation();
  const link = detail.conversation_link;
  if (link == null || !link.has_conversation) {
    return (
      <p data-testid="lead-conversation-none" className="text-body text-ink-2">
        {t('leadDetail.conversation.none')}
      </p>
    );
  }
  return (
    <a
      href={link.deep_link}
      data-testid="lead-conversation-link"
      className="inline-flex min-h-6 w-fit items-center gap-1 rounded text-body font-medium text-gold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus"
    >
      {t('leadDetail.conversation.open')} <span aria-hidden="true">→</span>
    </a>
  );
};
/**
 * Facts Mari captured for this lead (E12-S5.3, ADR-084 D5). Rendered in the
 * order the server sends (vocabulary order, then unknown keys) — no client
 * sort. Independent loading/error phase: a facts fetch failure never blanks
 * the rest of the page.
 */
const LeadFactsSection = ({
  facts,
  phase,
  locale,
}: {
  facts: LeadFact[];
  phase: LeadFactsPhase;
  locale: string;
}) => {
  const { t } = useTranslation();

  if (phase === 'loading') {
    return (
      <Skeleton data-testid="lead-facts-loading" label={t('leadDetail.loading')} className="h-24" />
    );
  }

  if (phase === 'error') {
    return (
      <Notice tone="danger" data-testid="lead-facts-error">
        {t('leadDetail.facts.error')}
      </Notice>
    );
  }

  if (facts.length === 0) {
    return <EmptyState data-testid="lead-facts-empty" title={t('leadDetail.facts.empty')} />;
  }

  return (
    <dl
      data-testid="lead-facts-list"
      className="grid grid-cols-1 gap-4 rounded-xl bg-surface-1 p-5 ring-1 ring-inset ring-ink-3/10 sm:grid-cols-2 lg:grid-cols-3"
    >
      {facts.map((fact) => (
        <div key={fact.key} data-testid="lead-fact-row" className="flex flex-col gap-0.5">
          <dt className="text-meta font-medium uppercase tracking-wide text-ink-3">{fact.label}</dt>
          <dd className="text-body text-ink-1">{fact.value}</dd>
          <dd className="text-meta text-ink-3">
            {t('leadDetail.facts.capturedAt')}:{' '}
            <time dateTime={fact.captured_at}>{formatDateTime(fact.captured_at, locale)}</time>
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const LeadDetailPage = ({ apiClient, leadId }: LeadDetailPageProps) => {
  const { t, i18n } = useTranslation();
  const { detail, phase, refetch } = useLeadDetail(apiClient, leadId);
  const { facts, phase: factsPhase } = useLeadFacts(apiClient, leadId);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  if (phase === 'loading' && detail == null) {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('leadDetail.back')} />
        <Skeleton
          data-testid="lead-detail-loading"
          label={t('leadDetail.loading')}
          className="h-48"
        />
      </section>
    );
  }

  if (phase === 'notFound') {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('leadDetail.back')} />
        <Notice tone="warning" data-testid="lead-detail-notfound">
          {t('leadDetail.notFound')}
        </Notice>
      </section>
    );
  }

  if (phase === 'error' && detail == null) {
    return (
      <section className="flex flex-col gap-4">
        <BackLink label={t('leadDetail.back')} />
        <Notice
          tone="danger"
          data-testid="lead-detail-error"
          action={
            <Button
              data-testid="lead-detail-retry"
              className="min-h-11"
              onClick={() => floating(refetch())}
            >
              {t('leadDetail.retry')}
            </Button>
          }
        >
          {t('leadDetail.errorBody')}
        </Notice>
      </section>
    );
  }

  if (detail == null) {
    // Defensive: ready phase always has a detail; satisfies the type guard.
    return null;
  }

  return (
    <section aria-labelledby="lead-detail-heading" className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <BackLink label={t('leadDetail.back')} />
        <header className="flex flex-wrap items-center gap-3">
          <h1 id="lead-detail-heading" className="text-h1 text-ink-1">
            {detail.display_name ?? t('leadDetail.unnamed')}
          </h1>
          <LeadStatusBadge status={detail.status} />
        </header>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="flex min-w-0 flex-col gap-6">
          <RecordSection detail={detail} locale={i18n.language} />

          <section aria-labelledby="lead-conversation-heading" className="flex flex-col gap-2">
            <h2 id="lead-conversation-heading" className="text-h2 text-ink-1">
              {t('leadDetail.conversation.heading')}
            </h2>
            <ConversationLinkBlock detail={detail} />
          </section>

          <section aria-labelledby="lead-facts-heading" className="flex flex-col gap-2">
            <h2 id="lead-facts-heading" className="text-h2 text-ink-1">
              {t('leadDetail.facts.heading')}
            </h2>
            <LeadFactsSection facts={facts} phase={factsPhase} locale={i18n.language} />
          </section>

          <section aria-labelledby="lead-timeline-heading" className="flex flex-col gap-2">
            <h2 id="lead-timeline-heading" className="text-h2 text-ink-1">
              {t('leadDetail.timeline.heading')}
            </h2>
            <LeadStateTimeline entries={detail.state_history} locale={i18n.language} />
          </section>
        </div>

        <section
          aria-labelledby="lead-transition-section"
          className="flex flex-col gap-2 rounded-xl bg-surface-1 p-4 ring-1 ring-inset ring-ink-3/10 lg:sticky lg:top-4"
        >
          <h2 id="lead-transition-section" className="text-h2 text-ink-1">
            {t('leadDetail.transition.sectionHeading')}
          </h2>
          <LeadTransitionForm
            apiClient={apiClient}
            leadId={detail.id}
            currentStatus={detail.status}
            allowedTransitions={detail.allowed_transitions}
            requiresNote={detail.status === 'quarantine'}
            onRefetch={refetch}
          />
        </section>
      </div>
    </section>
  );
};
